import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
} from '@mui/material';
import {
  ExpandMore,
  Security,
  Psychology,
  Analytics,
  Timeline,
  Assessment,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { CoinMarketData, GlobalData } from '../config/api';
import { fetchWithRetry, handleApiError } from '../utils/apiUtils';
import {
  calculateRSI,
  calculateMACD,
  calculateSMA,
  calculateVolatility,
  calculateATHDistance,
  formatPercentage,
  getColorByValue,
  calculateVolumeMarketCapRatio,
  calculateCirculatingSupplyRatio,
} from '../utils/tradingUtils';
import { colors, typography, spacing, borderRadius, shadows, transitions, componentStyles } from '../styles/designSystem';

const useStyles = makeStyles((theme) => ({
  panel: {
    background: colors.gradients.hero,
    padding: `${spacing['4xl']} 0`,
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at 80% 20%, rgba(230, 0, 122, 0.1) 0%, transparent 50%)',
      pointerEvents: 'none',
    },
  },
  
  sectionContainer: {
    ...componentStyles.contentContainer,
    position: 'relative',
    zIndex: 2,
  },
  
  title: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.black,
    fontSize: typography.fontSize['4xl'],
    color: colors.text.primary,
    marginBottom: spacing.md,
    letterSpacing: typography.letterSpacing.tight,
    background: colors.gradients.primary,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  
  subtitle: {
    fontFamily: typography.fontFamily.secondary,
    fontWeight: typography.fontWeight.regular,
    fontSize: typography.fontSize.lg,
    color: colors.text.secondary,
    maxWidth: '800px',
    margin: '0 auto',
    lineHeight: typography.lineHeight.relaxed,
    letterSpacing: typography.letterSpacing.normal,
    marginBottom: spacing['3xl'],
  },
  
  analysisCard: {
    ...componentStyles.glassCard,
    height: '100%',
    transition: transitions.smooth,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: shadows.xl,
    },
  },
  
  indicatorCard: {
    ...componentStyles.glassCard,
    padding: spacing.lg,
    marginBottom: spacing.md,
  },
  
  indicatorValue: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.black,
    fontSize: typography.fontSize['2xl'],
    marginBottom: spacing.sm,
  },
  
  indicatorLabel: {
    fontFamily: typography.fontFamily.secondary,
    fontWeight: typography.fontWeight.medium,
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: typography.letterSpacing.wider,
  },
  
  progressBar: {
    height: '8px',
    borderRadius: borderRadius.lg,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    '& .MuiLinearProgress-bar': {
      borderRadius: borderRadius.lg,
    },
  },
  
  riskLevel: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  
  sentimentChip: {
    fontFamily: typography.fontFamily.secondary,
    fontWeight: typography.fontWeight.semiBold,
    fontSize: typography.fontSize.sm,
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.xl,
    textTransform: 'uppercase',
    letterSpacing: typography.letterSpacing.wider,
  },
  
  bullish: {
    backgroundColor: colors.success.main,
    color: colors.text.primary,
  },
  
  bearish: {
    backgroundColor: colors.error.main,
    color: colors.text.primary,
  },
  
  neutral: {
    backgroundColor: colors.warning.main,
    color: colors.text.primary,
  },
  
  chartContainer: {
    height: '200px',
    marginTop: spacing.lg,
  },
  
  insightCard: {
    ...componentStyles.glassCard,
    padding: spacing.lg,
    marginBottom: spacing.md,
    border: `1px solid rgba(255, 255, 255, 0.1)`,
  },
  
  insightIcon: {
    color: colors.primary.main,
    marginBottom: spacing.sm,
  },
  
  accordion: {
    ...componentStyles.glassCard,
    '& .MuiAccordionSummary-root': {
      color: colors.text.primary,
      fontFamily: typography.fontFamily.primary,
      fontWeight: typography.fontWeight.semiBold,
    },
    '& .MuiAccordionDetails-root': {
      color: colors.text.secondary,
      fontFamily: typography.fontFamily.secondary,
    },
  },
}));

const TechnicalAnalysisPanel = ({ selectedCoin, onCoinSelect }) => {
  const classes = useStyles();
  const [technicalData, setTechnicalData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTechnicalData = useCallback(async (coinId) => {
    setLoading(true);
    setError(null);
    try {
      const [marketResponse] = await Promise.all([
        fetchWithRetry(CoinMarketData(coinId, 'usd')),
        fetchWithRetry(GlobalData())
      ]);

      const prices = marketResponse.data.prices.map(p => p[1]);
      const volumes = marketResponse.data.total_volumes.map(v => v[1]);
      
      const technicalIndicators = {
        rsi: calculateRSI(prices),
        macd: calculateMACD(prices),
        sma20: calculateSMA(prices, 20),
        sma50: calculateSMA(prices, 50),
        sma200: calculateSMA(prices, 200),
        volatility: calculateVolatility(prices),
        volumeChange: ((volumes[volumes.length - 1] - volumes[0]) / volumes[0]) * 100,
        priceChange: ((prices[prices.length - 1] - prices[0]) / prices[0]) * 100,
        athDistance: calculateATHDistance(prices[prices.length - 1], selectedCoin.ath),
        volumeMarketCapRatio: calculateVolumeMarketCapRatio(selectedCoin.total_volume, selectedCoin.market_cap),
        circulatingSupplyRatio: calculateCirculatingSupplyRatio(selectedCoin.circulating_supply, selectedCoin.total_supply),
      };

      setTechnicalData(technicalIndicators);
    } catch (err) {
      const errorInfo = handleApiError(err, 'Technical data fetch');
      setError('Failed to load technical data');
      console.error('Error fetching technical data:', errorInfo);
    } finally {
      setLoading(false);
    }
  }, [selectedCoin]);

  useEffect(() => {
    if (selectedCoin) {
      fetchTechnicalData(selectedCoin.id);
    }
  }, [selectedCoin, fetchTechnicalData]);

  const getRSIStatus = (rsi) => {
    if (rsi > 70) return { status: 'Overbought', color: colors.error.main, risk: 'High' };
    if (rsi < 30) return { status: 'Oversold', color: colors.success.main, risk: 'Low' };
    return { status: 'Neutral', color: colors.warning.main, risk: 'Medium' };
  };

  const getMACDSignal = (macd) => {
    if (!macd || !macd.macd || !macd.signal) return { signal: 'Neutral', color: colors.text.secondary };
    if (macd.macd > macd.signal) return { signal: 'Bullish', color: colors.success.main };
    return { signal: 'Bearish', color: colors.error.main };
  };

  const getMarketSentiment = (priceChange, volumeChange) => {
    if (priceChange > 5 && volumeChange > 10) return { sentiment: 'Strong Bullish', color: colors.success.main };
    if (priceChange > 2) return { sentiment: 'Bullish', color: colors.success.main };
    if (priceChange < -5 && volumeChange > 10) return { sentiment: 'Strong Bearish', color: colors.error.main };
    if (priceChange < -2) return { sentiment: 'Bearish', color: colors.error.main };
    return { sentiment: 'Neutral', color: colors.warning.main };
  };

  const getRiskLevel = (volatility, rsi) => {
    if (volatility > 50 || (rsi > 80 || rsi < 20)) return { level: 'High', color: colors.error.main };
    if (volatility > 25 || (rsi > 70 || rsi < 30)) return { level: 'Medium', color: colors.warning.main };
    return { level: 'Low', color: colors.success.main };
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  if (!selectedCoin) {
    return (
      <Box className={classes.panel}>
        <Container className={classes.sectionContainer}>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <motion.div variants={itemVariants}>
              <Box textAlign="center">
                <Typography className={classes.title}>
                  Technical Analysis Tools
                </Typography>
                <Typography className={classes.subtitle}>
                  Select a cryptocurrency from the table above to view detailed technical analysis, 
                  including RSI, MACD, moving averages, volatility metrics, and professional trading insights.
                </Typography>
                <Box sx={{ mt: 4 }}>
                  <Analytics sx={{ fontSize: 80, color: colors.primary.main, opacity: 0.5 }} />
                </Box>
              </Box>
            </motion.div>
          </motion.div>
        </Container>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box className={classes.panel}>
        <Container className={classes.sectionContainer}>
          <Card className={classes.analysisCard}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h5" sx={{ color: colors.text.primary, mb: 2 }}>
                Analyzing {selectedCoin.name}...
              </Typography>
              <LinearProgress className={classes.progressBar} />
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.panel}>
        <Container className={classes.sectionContainer}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  const rsiStatus = getRSIStatus(technicalData?.rsi);
  const macdSignal = getMACDSignal(technicalData?.macd);
  const marketSentiment = getMarketSentiment(technicalData?.priceChange, technicalData?.volumeChange);
  const riskLevel = getRiskLevel(technicalData?.volatility, technicalData?.rsi);

  return (
    <Box className={classes.panel}>
      <Container className={classes.sectionContainer}>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <motion.div variants={itemVariants}>
            <Box sx={{ mb: 4 }}>
              <Typography className={classes.title}>
                Technical Analysis: {selectedCoin.name}
              </Typography>
              <Typography className={classes.subtitle}>
                Professional trading indicators and market analysis for {selectedCoin.symbol.toUpperCase()}
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={3}>
            {/* Key Indicators */}
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <Card className={classes.analysisCard}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: colors.text.primary, mb: 3 }}>
                      Key Technical Indicators
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box className={classes.indicatorCard}>
                          <Typography className={classes.indicatorValue} style={{ color: rsiStatus.color }}>
                            {technicalData?.rsi?.toFixed(1) || 'N/A'}
                          </Typography>
                          <Typography className={classes.indicatorLabel}>
                            RSI (14)
                          </Typography>
                          <Chip 
                            label={rsiStatus.status}
                            className={classes.sentimentChip}
                            style={{ backgroundColor: rsiStatus.color }}
                            size="small"
                          />
                        </Box>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Box className={classes.indicatorCard}>
                          <Typography className={classes.indicatorValue} style={{ color: macdSignal.color }}>
                            {macdSignal.signal}
                          </Typography>
                          <Typography className={classes.indicatorLabel}>
                            MACD Signal
                          </Typography>
                          <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                            {technicalData?.macd?.macd?.toFixed(4) || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Box className={classes.indicatorCard}>
                          <Typography className={classes.indicatorValue} style={{ color: colors.text.primary }}>
                            {technicalData?.volatility?.toFixed(2) || 'N/A'}%
                          </Typography>
                          <Typography className={classes.indicatorLabel}>
                            Volatility (24h)
                          </Typography>
                          <Box className={classes.riskLevel}>
                            <Security sx={{ color: riskLevel.color, fontSize: 16 }} />
                            <Typography variant="caption" sx={{ color: riskLevel.color }}>
                              {riskLevel.level} Risk
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Box className={classes.indicatorCard}>
                          <Typography className={classes.indicatorValue} style={{ color: colors.text.primary }}>
                            {technicalData?.athDistance?.toFixed(1) || 'N/A'}%
                          </Typography>
                          <Typography className={classes.indicatorLabel}>
                            ATH Distance
                          </Typography>
                          <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                            From All-Time High
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* Market Analysis */}
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <Card className={classes.analysisCard}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: colors.text.primary, mb: 3 }}>
                      Market Analysis
                    </Typography>
                    
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 1 }}>
                        Market Sentiment
                      </Typography>
                      <Chip 
                        label={marketSentiment.sentiment}
                        className={classes.sentimentChip}
                        style={{ backgroundColor: marketSentiment.color }}
                        size="medium"
                      />
                    </Box>
                    
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 1 }}>
                        Volume/Market Cap Ratio
                      </Typography>
                      <Typography variant="h6" sx={{ color: colors.text.primary }}>
                        {technicalData?.volumeMarketCapRatio?.toFixed(2) || 'N/A'}%
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.min(technicalData?.volumeMarketCapRatio || 0, 100)} 
                        className={classes.progressBar}
                        sx={{ mt: 1 }}
                      />
                    </Box>
                    
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 1 }}>
                        Circulating Supply
                      </Typography>
                      <Typography variant="h6" sx={{ color: colors.text.primary }}>
                        {technicalData?.circulatingSupplyRatio?.toFixed(1) || 'N/A'}%
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={technicalData?.circulatingSupplyRatio || 0} 
                        className={classes.progressBar}
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* Moving Averages */}
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <Card className={classes.analysisCard}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: colors.text.primary, mb: 3 }}>
                      Moving Averages
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Box textAlign="center">
                          <Typography variant="h6" sx={{ color: colors.text.primary }}>
                            {technicalData?.sma20?.toFixed(2) || 'N/A'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                            SMA 20
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box textAlign="center">
                          <Typography variant="h6" sx={{ color: colors.text.primary }}>
                            {technicalData?.sma50?.toFixed(2) || 'N/A'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                            SMA 50
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box textAlign="center">
                          <Typography variant="h6" sx={{ color: colors.text.primary }}>
                            {technicalData?.sma200?.toFixed(2) || 'N/A'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                            SMA 200
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 1 }}>
                        Price Change (7d)
                      </Typography>
                      <Typography variant="h6" style={{ color: getColorByValue(technicalData?.priceChange) }}>
                        {formatPercentage(technicalData?.priceChange)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* Trading Insights */}
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <Card className={classes.analysisCard}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: colors.text.primary, mb: 3 }}>
                      Trading Insights
                    </Typography>
                    
                    <Accordion className={classes.accordion}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Psychology className={classes.insightIcon} />
                          <Typography>RSI Analysis</Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2">
                          {rsiStatus.status === 'Overbought' 
                            ? 'Asset may be overvalued. Consider taking profits or waiting for pullback.'
                            : rsiStatus.status === 'Oversold'
                            ? 'Asset may be undervalued. Potential buying opportunity.'
                            : 'RSI indicates neutral conditions. Monitor for breakout signals.'
                          }
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                    
                    <Accordion className={classes.accordion}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Timeline className={classes.insightIcon} />
                          <Typography>MACD Signal</Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2">
                          {macdSignal.signal === 'Bullish' 
                            ? 'MACD indicates upward momentum. Consider long positions.'
                            : macdSignal.signal === 'Bearish'
                            ? 'MACD indicates downward momentum. Consider short positions or wait.'
                            : 'MACD shows neutral momentum. Wait for clearer signals.'
                          }
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                    
                    <Accordion className={classes.accordion}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Assessment className={classes.insightIcon} />
                          <Typography>Risk Assessment</Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2">
                          Volatility is {riskLevel.level.toLowerCase()}. {riskLevel.level === 'High' 
                            ? 'Use smaller position sizes and tight stop-losses.'
                            : riskLevel.level === 'Medium'
                            ? 'Standard position sizing recommended.'
                            : 'Can use larger position sizes with wider stops.'
                          }
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default TechnicalAnalysisPanel; 